/**
 * 入札一覧サイドバーのフィルター見出し (通常入札 / ブロック入札 共用)。
 *
 * design 正: `design/bidding/spot.html` の `.sidebar-section-title`
 * (`.bid-list-sidebar` / `.block-list-sidebar` とも同値、spot.html:684 / :816)。
 *
 * ```css
 * display: flex; align-items: center; justify-content: space-between;
 * gap: var(--space-2); font-size: lg; font-weight: 700;
 * margin: 0 calc(-1 * var(--space-3));  ・ padding: var(--space-2) var(--space-3);
 * min-height: calc(40px + 2 * var(--space-2));  ・ border-bottom: 1px solid var(--border-low);
 * ```
 *
 * 押さえどころが 3 つある:
 *
 * 1. **左右の負 margin でサイドバーの padding を打ち消す**。下線をサイドバーの
 *    幅いっぱいに引くため (見出しだけ内側で切れると「区切り」に見えない)。
 * 2. **行の高さは 56px**。design は「隣の入札一覧ヘッダーと同じ 56px に統一」
 *    (spot.html:1254 のコメント) しており、左右の上端が揃う根拠がこの値。
 * 3. **見出しの右端に操作 (クリア) を置く** (`justify-content: space-between`)。
 *    design では JS が `#spot-bid-filter-reset` をこの行へ移動している
 *    (spot.html:12054)。フィルター群の末尾ではない。
 *
 * サイドバー側は `padding={3}` (= `--space-3`) であることが前提 (1. の負 margin が
 * それを打ち消す)。
 */
import type { ReactNode } from 'react'

import { useTranslation } from 'react-i18next'

import { Flex, Text } from '@enechain/ecloud-designsystem'

import { LIST_HEADER_HEIGHT } from '~/pages/SpotBidding/constants/listHeader'

type Props = {
  /** 見出し行の右端に置く操作 (クリア等)。省略時は見出しのみ。 */
  action?: ReactNode
  /**
   * 見出しの**左端**に置く操作 (開閉トグル)。省略時は見出しのみ。
   *
   * 畳んだ時のレールと同じ座標に来るよう、行の padding を負 margin で
   * 打ち消して列の左端まで届かせる (`FilterToggleButton` の JSDoc)。
   */
  leading?: ReactNode
}

export const SidebarFilterHeader = ({
  action,
  leading,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      height={LIST_HEADER_HEIGHT}
      marginTop={-3}
      borderBottomWidth="1px"
      borderBottomColor="Border.lowEmphasis"
      marginX={-3}
      paddingX={3}
      paddingY={2}
    >
      {/* 見出しと leading は**まとめて左**に寄せる。 `space-between` の直下に
          3 つ並べると見出しが中央へ流れてしまう。 */}
      {/* トグルとラベルの間隔 (デザイナー指摘 2026-09-16「もっと詰めたい」)。
          トグルは当たり判定 48px の中央に 32px の視覚ボックスを持つので、
          その右端は列の左端から 40px。 ここで gap を 0 にすると
          ラベルまで 8px (= space-2) になり、 DS のリズムに乗る。
          gap={2} のままだと 8 + 8 = 16px 空いて間延びする。 */}
      <Flex alignItems="center" gap={0} minWidth={0}>
        {/* 行の padding (X=3 / Y=2) を打ち消して、 トグルを列の左端・行の
            上下いっぱいに届かせる (レールと同じ座標にするため)。 */}
        {leading !== undefined && (
          <Flex alignSelf="stretch" marginLeft={-3} marginY={-2}>
            {leading}
          </Flex>
        )}
        {/* design ルール例外: filter まわりの見出しは Text.highEmphasis 固定
            (design/CLAUDE.md)。 */}
        <Text textStyle="sidebarSectionTitle" color="Text.highEmphasis">
          {t('page:biddingSpot.sidebar.filterTitle')}
        </Text>
      </Flex>
      {action}
    </Flex>
  )
}
